
import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { modelService } from '@/services/ai/modelService';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface AiChatBoxProps {
  onSubmit: (question: string) => void;
  onResponse: (response: string) => void;
  context?: string;
}

const AiChatBox = ({ onSubmit, onResponse, context = '' }: AiChatBoxProps) => {
  const [question, setQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || isLoading) return;

    const userQuestion = question;
    setQuestion('');
    setIsLoading(true);

    try {
      onSubmit(userQuestion);
      
      // Handle simple greetings locally
      const greeting = userQuestion.toLowerCase().trim();
      if (['hei', 'hallo', 'hi', 'hello'].includes(greeting)) {
        onResponse('Hei! Hva vil du vite om denne artikkelen?');
        return;
      }
      
      const response = await modelService.generateResponse(userQuestion, context);
      onResponse(response);
    } catch (error) {
      console.error('Feil ved generering av svar:', error);
      toast.error('Kunne ikke generere svar. Vennligst prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={t('ai.askKaiPlaceholder', 'Ask Kai a question...')}
            className="w-full p-3 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ea384c] focus:border-transparent"
            disabled={isLoading}
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-[#ea384c] disabled:opacity-50"
            disabled={isLoading}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default AiChatBox;
